import { cn } from '@neo/lib/utils'
import { Button } from '@neo/primitives/button'
import { RefreshCcw, Unplug } from 'lucide-react'
import { Component, type ErrorInfo, type ReactNode } from 'react'
import { getDefaultRoute } from 'v2/configs/routes'
import { logger } from 'v2/utils/logger'

interface ErrorFallbackProps {
  className?: string
}

const ErrorFallback = ({ className }: ErrorFallbackProps) => (
  <div role="alert" className={cn('h-full overflow-y-auto', className)}>
    <div className="flex min-h-full items-center justify-center p-6">
      <div className="flex w-full max-w-sm flex-col items-start gap-6">
        <div className="flex size-12 items-center justify-center rounded-lg border border-st2 bg-bg2">
          <Unplug className="size-6 text-secondary" aria-hidden="true" />
        </div>
        <div className="flex flex-col gap-3">
          <p className="font-jetbrains text-label uppercase text-tertiary">Application error</p>
          <h2 className="text-h2-500 text-primary">Something went wrong</h2>
          <p className="text-p-400 text-secondary">
            This view could not load. Refresh to try again, or return to your home page.
          </p>
        </div>
        <div className="flex w-full flex-wrap items-center gap-3 border-t border-st2 pt-6">
          <Button
            size="sm"
            className="gap-2"
            data-testid="button-refresh"
            onClick={() => window.location.reload()}
          >
            <RefreshCcw className="size-4" />
            Refresh
          </Button>
          <Button
            size="sm"
            variant="outline"
            data-testid="button-take-me-home"
            onClick={() => {
              window.location.href = getDefaultRoute()
            }}
          >
            Take me home
          </Button>
        </div>
      </div>
    </div>
  </div>
)

interface Props {
  children: ReactNode
  fallback?: ReactNode
  className?: string
}

interface State {
  hasError: boolean
  error?: Error
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.critical(error.message, error, errorInfo)
  }

  render() {
    const { hasError } = this.state
    const { children, fallback, className } = this.props

    if (hasError) {
      if (fallback) return fallback

      return <ErrorFallback className={className} />
    }

    return children
  }
}
